<template>
  <v-app class="verofresh">
    <top-bar ref="top-bar" @nav-icon-click="toggleNav"/>
    <v-main>
      <div class="flex-container" v-bind:class="{ slideInSideNav: slideInSideNav }">
        <v-overlay v-model="showCustomerContextModal">
          <v-card style="width: 600px">
            <v-card-title>{{ $t('choose_account') }}</v-card-title>
            <v-card-subtitle>{{ $t('choose_account_subtext') }}</v-card-subtitle>
            <customer-context-selector
              ref="customerContextSelector"
              @change-context="showCustomerContextModal = false"
              :height-adjustment="200" />
          </v-card>
        </v-overlay>
        <div v-if="!appReady">
          <h4 class="my-4">Please wait while we prepare the application…</h4>
          <v-progress-linear indeterminate/>
        </div>
        <side-nav v-if="appReady && !user.can('view_display')" class="side-nav primary-900" ref="side-nav"/>
        <div v-if="appReady" class="main-content" ref="main-content">
          <remote-banners ref="remote-banners" :customer-id="this.customerContext.id" />
          <app-banners ref="app-banners"/>
          <app-notifications ref="app-notifications"/>
          <v-dialog ref="user_terms" v-model="showUserTerms" width="90%" persistent>
            <v-card>
              <v-card-title v-html="$t('verofresh_terms_modal_title')"/>
              <v-divider/>
              <v-card-text>
                <user-terms/>
              </v-card-text>
              <v-divider/>
              <v-card-actions>
                <v-spacer/>
                <v-btn @click="handleUserTermsDisagree" class="mr-2">{{ $t('disagree') }}</v-btn>
                <v-btn color="primary" @click="handleUserTermsAgree">{{ $t('agree') }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <router-view />
        </div>
        <session-timeout-modal v-if="!maintenanceMode"/>
      </div>
    </v-main>
    <!-- Don't show any globally dispatched modals if we're in maintenance mode. -->
    <global-modal v-if="!maintenanceMode"/>
    <maintenance-modal/>
  </v-app>
</template>

<script>

import TopBar from '@/components/navigation/TopBar'
import SideNav from '@/components/navigation/SideNav'
import RemoteBanners from '@/components/shared/RemoteBanners'
import AppBanners from '@/components/site/AppBanners'
import AppNotifications from '@/components/site/AppNotifications'
import GlobalModal from '@/components/shared/GlobalModal'
import MaintenanceModal from '@/components/shared/MaintenanceModal'
import SessionTimeoutModal from '@/components/shared/SessionTimeoutModal'
import UserTerms from '@/components/site/UserTerms'
import AuthUtil from '@/util/AuthUtil'
import { setLocaleAsync } from '@/i18n'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import LocaleMixin from '@/components/mixins/LocaleMixin'
import CustomerContextSelector from '@/components/navigation/CustomerContextSelector'
import { map, includes } from 'lodash'

export default {
  name: 'AppLayout',
  components: {
    TopBar,
    SideNav,
    RemoteBanners,
    AppBanners,
    AppNotifications,
    GlobalModal,
    MaintenanceModal,
    SessionTimeoutModal,
    UserTerms,
    CustomerContextSelector
  },
  mixins: [CurrentUserMixin, LocaleMixin],
  data () {
    return {
      showCustomerContextModal: false,
      showUserTerms: false,
      slideInSideNav: false
    }
  },
  computed: {
    /**
     * Prevent the layout from rendering if we have not yet loaded the user and translations, or have not selected a
     * customer context. This prevents the app from momentarily displaying UI and then redirecting/hiding/showing other
     * UI elements.
     *
     * @return Boolean
     */
    appReady () {
      return this.userLoaded && this.hasCustomerContext && this.translationsLoaded
    },
    /**
     * Convenience property for whether we are currently in maintenance mode
     * @returns {*}
     */
    maintenanceMode () {
      return this.$store.getters['globalMessagingModule/maintenanceMode']
    }
  },
  methods: {
    /**
     * Send a user to the Vue 3 UI while the lock is on: root users when
     * feature.forceNewUIForRoot.enabled is on, and anyone when
     * feature.forceNewUI.enabled is on. Keeps the same path so deep links
     * (emails, bookmarks) still land on the equivalent page. `replace` keeps the
     * classic URL out of history so Back doesn't bounce between the two apps.
     * The decision lives here, in the classic app, so turning the flags off
     * always restores it even if the new UI is broken.
     *
     * @param {Promise} featuresFetched the fetchFeatures dispatch
     */
    async redirectIfClassicUiLocked (featuresFetched) {
      try {
        await featuresFetched
      } catch (e) {
        return
      }
      const rootLocked = this.isRootUser && this.$store.getters['featureModule/forceNewUIForRoot']
      if (rootLocked || this.$store.getters['featureModule/forceNewUI']) {
        window.location.replace('/v3' + this.$route.fullPath)
      }
    },
    toggleNav () {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
          this.toggleSideNavSlideIn()
          break
        case 'sm':
          this.toggleSideNavSlideIn()
          break
        case 'md':
          this.$refs['side-nav'].toggleMinimizeNav()
          break
        case 'lg':
          this.$refs['side-nav'].toggleMinimizeNav()
          break
        case 'xl':
          this.$refs['side-nav'].toggleMinimizeNav()
          break
      }
    },
    toggleSideNavSlideIn () {
      this.slideInSideNav = !this.slideInSideNav
    },
    loadBanners () {
      if (this.$refs['remote-banners'] !== undefined) {
        this.$refs['remote-banners'].loadBanners()
      }
    },
    /**
     * Loads user details into the app (either from the local module cache or from the remote REST end-point)
     */
    async fetchUser () {
      if (!this.userLoaded) {
        const expands = [
          'customers',
          'roles',
          'role_permissions',
          'user_settings'
        ]
        const fields = [
          '*',
          'role_permissions.scope',
          'role_permissions.permission_code',
          'user_settings.key',
          'user_settings.value',
          'customers.id',
          'customers.name',
          'customers.parent_id',
          'roles.id',
          'roles.code',
          'roles.name'
        ]
        const user = await this.$orm('User').find('self', {
          params: {
            expand: expands.join(','),
            fields: fields.join(',')
          }
        })
        await this.$store.dispatch('userModule/setUser', user)
        // If the previously set customer context isn't in this user's available customers, reset the context.
        const customerIds = map(user.customers, x => x.id)
        if (includes(customerIds, this.customerContextId) === false) {
          await this.$store.dispatch('userModule/resetCustomerContext')
        }
        setLocaleAsync(user.getSetting('default_language', 'en'))
        this.ensureTermsAreAccepted(user)
      }
    },
    ensureTermsAreAccepted () {
      this.showUserTerms = this.user.getSetting('verofreshTermsAccepted', 0) < UserTerms.data().version
    },
    handleUserTermsAgree () {
      this.user.updateSettings({ verofreshTermsAccepted: UserTerms.data().version })
      this.showUserTerms = false
    },
    handleUserTermsDisagree () {
      AuthUtil.logout()
      this.$router.push('/login')
    },
    /**
     * If user does not have a customer context selected, force them to pick one via the modal.
     */
    ensureCustomerContext () {
      // If we already have a customer context, or the user isn't loaded yet, exit early.
      if (this.hasCustomerContext || !this.userLoaded) {
        return
      }
      // If the user only has one customer then we auto set the customer context. Otherwise, if there is more than one
      // customer and the customer context modal is ready show it (if it's not then the mounted method below will fire
      // when ready)
      if (this.user.customers.length === 1) {
        this.$store.dispatch('userModule/changeCustomerContext', this.user.customers[0].id)
      } else {
        this.showCustomerContextModal = true
      }
    }
  },
  async mounted () {
    await this.fetchUser()
    // Fetch the resolved feature flags on every boot/login/session creation —
    // NOT gated on userLoaded, since the flags aren't part of the persisted
    // user blob and must be re-resolved for the current session/context
    // (VF-4173). Customer switches re-fetch via changeCustomerContext.
    this.redirectIfClassicUiLocked(this.$store.dispatch('featureModule/fetchFeatures'))
    setLocaleAsync(this.user.getSetting('default_language', 'en'))
    this.ensureTermsAreAccepted(this.user)
    this.ensureCustomerContext()
  },
  watch: {
    $route (to, from) {
      this.slideInSideNav = false
      this.loadBanners()
    },
    user: function (newUser, oldUser) {
      this.ensureTermsAreAccepted(newUser)
      this.ensureCustomerContext()
    }
  }
}
</script>

<style scoped lang="scss">
  // Temporary: Hack the base font-size to be 14px on pages that use this layout only. (normally 16px in Material Design)
  // Once we ditch bootstrap this should be part of the main app.scss.
  // Never mind. This apparently still leaks out to normal pages (probably the style just gets put in a block somewhere)
  // Doesn't work when scoped either, because it needs to be applied to the root of the doc.
  html {
    font-size: 14px !important;
  }
  .flex-container {
    display: flex;
    flex: 1 0 auto;
  }
  .side-nav {
    flex: 0;
    min-height: calc(100vh - 64px);
  }
  .main-content {
    margin-left: 10px;
    flex: 1;
    overflow: auto;
  }
  // Change sidebar behavior on smaller screens. This places the sidebar off to the left at a fixed width of 270px
  // and slides it in when the "slideInSideNav" class is applied to the flex container.
  @media screen and (max-width: 960px) {
    .side-nav {
      width: 270px;
      flex: 0 0 270px;
    }
    .flex-container {
      position: relative;
      left: 0;
      margin-left: -270px;
      transition: left 0.5s ease;
    }
    .flex-container.slideInSideNav {
      left: 270px;
    }
    .main-content {
      position: relative;
    }
  }
</style>
